// Auto-generated lightweight class for MBSE_Port
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { MBSE_PortReadAPI, MBSE_PortWriteAPI, MBSE_PortReadAPISchema, MBSE_PortWriteAPISchema } from '../types/mbse_port';
import { ModelRegistry } from '../../registries/model-registry';
import { AtScope, HasMembers, MBSE_Attributes, MBSE_Delegation, MBSE_Flows, MBSE_Ports, MBSE_Satisfaction } from '../../relationships/relationship';

import { SecurityScope } from '../../shared/api/securityscope';
import { SysMLNamespace } from '../../sysml/api/sysmlnamespace';
import { MBSE_Action } from './mbse_action';
import { MBSE_Requirement } from './mbse_requirement';
import { MBSE_Attribute } from './mbse_attribute';



export interface MBSE_Port extends Partial<MBSE_PortReadAPI> {}
export class MBSE_Port extends NeoNodeBase<MBSE_PortReadAPI, MBSE_PortWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "MBSE_Port";
  static readonly readSchema = MBSE_PortReadAPISchema;
  static readonly writeSchema = MBSE_PortWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    member_namespace: { target: "SysMLNamespace", cardinality: "one", isParent: false, relClass: "HasMembers", direction: "in" },
    owner: { target: "MBSE_Action", cardinality: "one", isParent: false, relClass: "MBSE_Ports", direction: "in" },
    requirements: { target: "MBSE_Requirement", cardinality: "many", isParent: false, relClass: "MBSE_Satisfaction", direction: "in" },
    attributes: { target: "MBSE_Attribute", cardinality: "many", isParent: false, relClass: "MBSE_Attributes", direction: "out" },
    flows_to: { target: "MBSE_Port", cardinality: "many", isParent: false, relClass: "MBSE_Flows", direction: "out" },
    flows_from: { target: "MBSE_Port", cardinality: "many", isParent: false, relClass: "MBSE_Flows", direction: "in" },
    delegates_on: { target: "MBSE_Port", cardinality: "many", isParent: false, relClass: "MBSE_Delegation", direction: "out" },
    delegate_of: { target: "MBSE_Port", cardinality: "many", isParent: false, relClass: "MBSE_Delegation", direction: "in" },
  };

  protected get modelName(): string {
    return 'MBSE_Port';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return MBSE_Port.label;
  }


  constructor(data: Partial<MBSE_PortWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: MBSE_PortReadAPI, loadContext?: Record<string, any>): MBSE_Port {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { security_scope, member_namespace, owner, requirements, attributes, flows_to, flows_from, delegates_on, delegate_of, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = MBSE_PortReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, security_scope: security_scope, member_namespace: member_namespace, owner: owner, requirements: requirements, attributes: attributes, flows_to: flows_to, flows_from: flows_from, delegates_on: delegates_on, delegate_of: delegate_of };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return MBSE_Port.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadOneRelationship(processed, 'member_namespace', 'SysMLNamespace', ModelRegistry);
    loadOneRelationship(processed, 'owner', 'MBSE_Action', ModelRegistry);
    loadManyRelationship(processed, 'requirements', 'MBSE_Requirement', ModelRegistry);
    loadManyRelationship(processed, 'attributes', 'MBSE_Attribute', ModelRegistry);
    loadManyRelationship(processed, 'flows_to', 'MBSE_Port', ModelRegistry);
    loadManyRelationship(processed, 'flows_from', 'MBSE_Port', ModelRegistry);
    loadManyRelationship(processed, 'delegates_on', 'MBSE_Port', ModelRegistry);
    loadManyRelationship(processed, 'delegate_of', 'MBSE_Port', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new MBSE_Port(processed as any);

    return instance;
  }


  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'member_namespace' - Python ORM style (cached)
  get member_namespace(): RelationshipManager<this, SysMLNamespace> {
    return this.getOrCreateRelationshipManager('member_namespace', () => {
      return new RelationshipManager(this, 'member_namespace', 'one', HasMembers as typeof Relationship);
    });
  }

  // Relationship manager for 'owner' - Python ORM style (cached)
  get owner(): RelationshipManager<this, MBSE_Action> {
    return this.getOrCreateRelationshipManager('owner', () => {
      return new RelationshipManager(this, 'owner', 'one', MBSE_Ports as typeof Relationship);
    });
  }

  // Relationship manager for 'requirements' - Python ORM style (cached)
  get requirements(): RelationshipManager<this, MBSE_Requirement> {
    return this.getOrCreateRelationshipManager('requirements', () => {
      return new RelationshipManager(this, 'requirements', 'many', MBSE_Satisfaction as typeof Relationship);
    });
  }

  // Relationship manager for 'attributes' - Python ORM style (cached)
  get attributes(): RelationshipManager<this, MBSE_Attribute> {
    return this.getOrCreateRelationshipManager('attributes', () => {
      return new RelationshipManager(this, 'attributes', 'many', MBSE_Attributes as typeof Relationship);
    });
  }

  // Relationship manager for 'flows_to' - Python ORM style (cached)
  get flows_to(): RelationshipManager<this, MBSE_Port> {
    return this.getOrCreateRelationshipManager('flows_to', () => {
      return new RelationshipManager(this, 'flows_to', 'many', MBSE_Flows as typeof Relationship);
    });
  }

  // Relationship manager for 'flows_from' - Python ORM style (cached)
  get flows_from(): RelationshipManager<this, MBSE_Port> {
    return this.getOrCreateRelationshipManager('flows_from', () => {
      return new RelationshipManager(this, 'flows_from', 'many', MBSE_Flows as typeof Relationship);
    });
  }

  // Relationship manager for 'delegates_on' - Python ORM style (cached)
  get delegates_on(): RelationshipManager<this, MBSE_Port> {
    return this.getOrCreateRelationshipManager('delegates_on', () => {
      return new RelationshipManager(this, 'delegates_on', 'many', MBSE_Delegation as typeof Relationship);
    });
  }

  // Relationship manager for 'delegate_of' - Python ORM style (cached)
  get delegate_of(): RelationshipManager<this, MBSE_Port> {
    return this.getOrCreateRelationshipManager('delegate_of', () => {
      return new RelationshipManager(this, 'delegate_of', 'many', MBSE_Delegation as typeof Relationship);
    });
  }

}
